/**
 * userTeamStore — backend-backed shim preserving the old function names so
 * consumers that already imported `setUserTeam`, `getUserTeam`,
 * `getAllUserTeams`, and `onUserTeamsChange` keep working without churn.
 *
 * Reads now come from Redux (`state.companyReducer.usersDictionary`) so
 * `user.team` is the canonical source. Writes dispatch the saga that calls
 * `PATCH /user/:id/team` and then refreshes the users dictionary.
 *
 * `onUserTeamsChange` is kept as a no-op subscriber for backward
 * compatibility — UI re-renders are now driven by Redux state changes via
 * `useSelector`, so the explicit pub/sub is no longer required.
 */

import store from "../../store";
import { assignUserTeam } from "../../redux/teams/actions";

/** Read a user's team from the Redux user dictionary. */
export function getUserTeam(userId, fallback = "") {
  if (!userId) return fallback;
  const state = store.getState();
  const dict = state?.companyReducer?.usersDictionary || {};
  return dict[String(userId)]?.team || fallback;
}

/** Write a user's team via the API saga. Fire-and-forget; saga handles
 *  optimistic counts + refetch + rollback-on-failure. */
export function setUserTeam(userId, team) {
  if (!userId) return;
  store.dispatch(assignUserTeam(userId, team || ""));
}

/** Snapshot { userId: teamName } from Redux. Kept for backward compatibility. */
export function getAllUserTeams() {
  const state = store.getState();
  const dict = state?.companyReducer?.usersDictionary || {};
  const out = {};
  Object.keys(dict).forEach((id) => {
    const t = dict[id]?.team;
    if (t) out[id] = t;
  });
  return out;
}

/**
 * No-op subscriber — Redux is the source of truth now. Consumers should
 * prefer `useSelector(state => state.companyReducer.usersDictionary)` or
 * the higher-level `UseUsersFullData()` hook. Returns an unsubscribe.
 */
export function onUserTeamsChange(_handler) {
  return () => {};
}
